import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Chip,
  Checkbox,
  AppBar,
  Toolbar,
  Avatar,
  Container,
  Alert,
  Snackbar,
  InputAdornment,
} from "@mui/material";
import {
  Save as SaveIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import axios from "axios";
import FileUploadComponent from "../../../components/FileUploadComponent";

const AssessorBioData = () => {
  const navigate = useNavigate();
  const [assessorOrgId, setAssessorOrgId] = useState(
    localStorage.getItem("orgId") || ""
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [loading, setLoading] = useState(false);
  const [isOrgIdExist, setIsOrgIdExist] = useState(false);
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [assessorStatus, setAssessorStatus] = useState(null);
  const [isFormEditable, setIsFormEditable] = useState(true);
  const [isFormEditableForPan, setIsFormEditableForPan] = useState(true);
  const [verifyingLabId, setVerifyingLabId] = useState(false);
  const [labVerified, setLabVerified] = useState(false);
  const [verifiedLabData, setVerifiedLabData] = useState(null);

  // Load Assessor Basic Details from API
  const loadAssessorDetails = async (orgId) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/get-basic-details/${orgId}`;
      const resp = await axios.get(url);
      console.log("Assessor Details Response:", resp.data);
      if (resp.data.success && resp.data.data) {
        const data = resp.data.data;
        if (data.OrgId) {
          setIsOrgIdExist(true);
        }

        // Set assessor status
        setAssessorStatus(data.status);

        // Check if required fields are filled (treat null, undefined, and empty string as not filled)
        const requiredFieldsFilled =
          data.residentialLatitude && data.residentialLatitude !== null &&
          data.residentialLongitude && data.residentialLongitude !== null &&
          data.officeLatitude && data.officeLatitude !== null &&
          data.officeLongitude && data.officeLongitude !== null &&
          //data.exemptFromAllotment && data.exemptFromAllotment !== "" &&
          data.orgNABLaccridiated !== null &&
          data.empStatus !== null &&
          (data.orgNABLaccridiated === "Yes" ?
            (data.FieldType && data.FieldType !== null &&
              data.Discipline && data.Discipline !== null && data.Labid && data.Labid !== null)
            : true);

            const canEditPan = !data.UploadCencelCheque || !data.UploadPanFile
        // Form is editable if:
        // 1. Status is not 1 (normal edit mode), OR
        // 2. Status is 1 AND required fields are NOT filled (can fill GPS/NABL)
        const canEdit = data.status !== 1 || (data.status === 1 && !requiredFieldsFilled);
        setIsFormEditable(canEdit);
        setIsFormEditableForPan(canEditPan)

        // Convert DOB to YYYY-MM-DD format (handles both MM/DD/YYYY and YYYY-MM-DD formats)
        let formattedDob = "";
        if (data.DOB) {
          // Check if it's already in YYYY-MM-DD format
          if (data.DOB.includes('-') && data.DOB.match(/^\d{4}-\d{2}-\d{2}$/)) {
            formattedDob = data.DOB;
          } else if (data.DOB.includes('/')) {
            // Convert MM/DD/YYYY to YYYY-MM-DD
            const parts = data.DOB.split('/');
            if (parts.length === 3) {
              formattedDob = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
            }
          }
        }

        setFormData({
          officeLatitude: data.officeLatitude || "",
          officeLongitude: data.officeLongitude || "",
          residentialLatitude: data.residentialLatitude || "",
          residentialLongitude: data.residentialLongitude || "",
          isNablAccredited: data.orgNABLaccridiated,
          nablAccreditationType: data.FieldType,
          nablDisciplines: [], // Will be set after auto-verifying LAB-ID
          labId: data.Labid || "",
          labname: data.labname || "",
          exemptFromAllotment: data.exemptFromAllotment || "",
          panCardFile: data.UploadPanFile
            ? {
              fileName: data.UploadPanFile,
              key: `live/${data.UploadPanFile}`,
              originalName: data.UploadPanFile,
            }
            : null,

          bankProofFile: data.UploadCencelCheque
            ? {
              fileName: data.UploadCencelCheque,
              key: `live/${data.UploadCencelCheque}`,
              originalName: data.UploadCencelCheque,
            }
            : null,
        });
        // Auto-verify LAB-ID if present to load discipline names
        if (data.Labid && data.FieldType) {
          setLabVerified(true);
          try {
            const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Checklabid?labid=${data.Labid}`;
            const response = await axios.get(apiUrl);

            if (response.data && response.data.isstatus && response.data.lab && Array.isArray(response.data.lab) && response.data.lab.length > 0) {
              const labData = response.data.lab[0];
              setVerifiedLabData(labData);

              // Parse disciplines from comma-separated string
              const disciplineNames = labData.disciplines ? labData.disciplines.split(',').map(d => d.trim()) : [];
              const disciplinesArray = disciplineNames.map((name, index) => ({
                id: `${index + 1}`,
                disciplineName: name,
              }));

              setAvailableDisciplines(disciplinesArray);

              // Map saved discipline IDs from database to the verified discipline IDs
              const savedDisciplineIds = data.Discipline ? (typeof data.Discipline === 'string' ? data.Discipline.split(',').map(id => id.trim()) : data.Discipline) : [];

              // Update formData with saved disciplines after availableDisciplines is set
              setFormData(prevData => ({
                ...prevData,
                nablDisciplines: savedDisciplineIds,
              }));

              console.log("✅ Auto-verified LAB-ID and loaded disciplines:", disciplinesArray);
              console.log("✅ Mapped saved disciplines:", savedDisciplineIds);
            }
          } catch (error) {
            console.error("Error auto-verifying LAB-ID:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error loading assessor details:", error);
    }
  };

  useEffect(() => {
    loadAssessorDetails(assessorOrgId);
  }, []);

  // Verify LAB-ID from NABL API
  const verifyLabId = async () => {
    if (!formData.labId || !formData.nablAccreditationType) {
      showSnackbar("Please enter LAB-ID and select Accreditation Type first", "error");
      return;
    }

    setVerifyingLabId(true);
    setLabVerified(false);
    setVerifiedLabData(null);

    try {
      const apiUrl = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Checklabid?labid=${formData.labId}`;
      console.log("Verifying LAB-ID:", apiUrl);

      const response = await axios.get(apiUrl);
      console.log("LAB-ID Verification Response:", response.data);

      // Handle new response structure: { isstatus: true, message: "...", lab: [{...}] }
      if (response.data && response.data.isstatus && response.data.lab && Array.isArray(response.data.lab) && response.data.lab.length > 0) {
        const labData = response.data.lab[0];
        setVerifiedLabData(labData);
        setLabVerified(true);

        // Parse disciplines from comma-separated string (field name is "disciplines")
        const disciplineNames = labData.disciplines ? labData.disciplines.split(',').map(d => d.trim()) : [];
        const disciplinesArray = disciplineNames.map((name, index) => ({
          id: `${index + 1}`,
          disciplineName: name,
        }));

        setAvailableDisciplines(disciplinesArray);

        // Auto-select all disciplines and update formData
        const allDisciplineIds = disciplinesArray.map(d => d.id);
        setFormData(prevData => ({
          ...prevData,
          labname: labData.name || "",
          nablDisciplines: allDisciplineIds, // Auto-bind all disciplines
        }));

        showSnackbar(`LAB-ID verified successfully! Lab: ${labData.name}`, "success");
      } else {
        showSnackbar("LAB-ID not found or invalid", "error");
        setAvailableDisciplines([]);
      }
    } catch (error) {
      console.error("Error verifying LAB-ID:", error);
      showSnackbar("Failed to verify LAB-ID. Please check the LAB-ID and try again.", "error");
      setAvailableDisciplines([]);
    } finally {
      setVerifyingLabId(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Enhanced Basic Details State
  const [formData, setFormData] = useState({
    isNablAccredited: "",
    nablAccreditationType: "",
    nablDisciplines: [],
    officeLatitude: "",
    officeLongitude: "",
    residentialLatitude: "",
    residentialLongitude: "",
    labId: "",
    labname: "",
    exemptFromAllotment: "",
    panCardFile: "",
    bankProofFile: "",
  });

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  // Get current location for Office Address
  const getOfficeCurrentLocation = () => {
    if (!navigator.geolocation) {
      showSnackbar("Geolocation is not supported by this browser", "error");
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);

        setFormData({
          ...formData,
          officeLatitude: lat,
          officeLongitude: lng,
        });

        const accuracy = position.coords.accuracy;
        const accuracyText = accuracy < 100 ? 'high accuracy' : accuracy < 500 ? 'medium accuracy' : 'low accuracy';
        showSnackbar(`Office location detected (${accuracyText}: ±${Math.round(accuracy)}m)`, "success");
        setLoading(false);
      },
      (error) => {
        let errorMessage = 'Failed to get current location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please allow location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location unavailable. Please try again or enter manually.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
        }
        showSnackbar(errorMessage, "error");
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  // Get current location for Residential Address
  const getResidentialCurrentLocation = () => {
    if (!navigator.geolocation) {
      showSnackbar("Geolocation is not supported by this browser", "error");
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);

        setFormData({
          ...formData,
          residentialLatitude: lat,
          residentialLongitude: lng,
        });

        const accuracy = position.coords.accuracy;
        const accuracyText = accuracy < 100 ? 'high accuracy' : accuracy < 500 ? 'medium accuracy' : 'low accuracy';
        showSnackbar(`Residential location detected (${accuracyText}: ±${Math.round(accuracy)}m)`, "success");
        setLoading(false);
      },
      (error) => {
        let errorMessage = 'Failed to get current location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please allow location access.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location unavailable. Please try again or enter manually.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
        }
        showSnackbar(errorMessage, "error");
        setLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      // Validate mandatory fields for location and lab details
      const mandatoryFields = [
        { field: formData.officeLatitude, name: "Office Latitude" },
        { field: formData.officeLongitude, name: "Office Longitude" },
        { field: formData.residentialLatitude, name: "Residential Latitude" },
        { field: formData.residentialLongitude, name: "Residential Longitude" },
        { field: formData.isNablAccredited, name: "NABL Accredited" },
        //{ field: formData.exemptFromAllotment, name: "Exemption from Allotment Validation" },
        ...(formData.isNablAccredited === "Yes" ? [
          { field: formData.nablAccreditationType, name: "NABL Accreditation Type" },
          { field: formData.nablDisciplines, name: "NABL Disciplines" },
          { field: formData.labId, name: "LAB ID" },
        ] : []),
      ];

      const missingFields = mandatoryFields
        .filter(({ field }) => !field || field === "")
        .map(({ name }) => name);

      if (missingFields.length > 0) {
        showSnackbar(
          `Please fill all mandatory fields: ${missingFields.join(", ")}`,
          "error"
        );
        setLoading(false);
        return;
      }

      // Prepare payload for location and lab details update
      const payload = {
        OrgId: assessorOrgId,
        residentialLatitude: formData.residentialLatitude,
        residentialLongitude: formData.residentialLongitude,
        officeLatitude: formData.officeLatitude,
        officeLongitude: formData.officeLongitude,
        orgNABLaccridiated: formData.isNablAccredited,
        FieldType: formData.nablAccreditationType,
        Discipline: (() => {
          // Map discipline IDs to names for saving
          if (Array.isArray(formData.nablDisciplines)) {
            const disciplineNames = formData.nablDisciplines.map(id => {
              const discipline = availableDisciplines.find(d => String(d.id) === String(id));
              return discipline ? discipline.disciplineName : id;
            });
            return disciplineNames.join(',');
          }
          return formData.nablDisciplines;
        })(),
        Labid: formData.labId,
        labname: formData.labname,
        exemptFromAllotment: formData.exemptFromAllotment,
        PanUploadFile: formData.panCardFile?.fileName || "",
        CancelChequeUploadfile: formData.bankProofFile?.fileName || "",
      };

      // Call the new update-location-lab-details API
      const url = `${process.env.REACT_APP_API_URL}/assessor-biodata/update-location-lab-details`;
      const resp = await axios.put(url, payload);

      if (resp.data.success) {
        showSnackbar("Location and lab details updated successfully!", "success");
        console.log("Form data:", resp.data);
      } else {
        showSnackbar("Failed to update location and lab details", "error");
      }
    } catch (error) {
      console.error("Error saving basic details:", error);
      showSnackbar("Error saving basic details", "error");
    } finally {
      setLoading(false);
    }
  };


  // Enhanced Basic Details Section
  const renderBasicDetails = () => (
    <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
      <CardContent sx={{ p: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4, justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
              <PersonIcon />
            </Avatar>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Assessor Basic Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please fill in your personal and contact information
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleSkip}
            size="medium"
            sx={{ textTransform: 'none' }}
          >
            Skip for Now
          </Button>
        </Box>

        <Box
          sx={{
            mb: 3,
            p: 2,
            backgroundColor: "primary.light",
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "primary.contrastText" }}
          >
            Assessor ID: {localStorage.getItem("username")}
          </Typography>
        </Box>

        {/* Instructions Card */}
        <Card
          variant="outlined"
          sx={{ mb: 3, border: "2px solid", borderColor: "warning.main" }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <InfoIcon sx={{ color: "warning.main", mr: 1, fontSize: 28 }} />
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "warning.main" }}
              >
                Instructions for Assessor Profile
              </Typography>
            </Box>

            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                1. Navigate to the "Basic Details" section in your profile and
                ensure all information is thoroughly reviewed and updated.
              </Typography>

              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                2. Mark "Yes" or "No" for the question: "Is your current
                organization NABL accredited?"
              </Typography>

              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                3. Enter the latitude and longitude for both your office and
                residence.
              </Typography>

              <Box sx={{ pl: 2, mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "medium", color: "text.secondary" }}
                >
                  A.) If they are at different locations, please obtain the
                  correct coordinates for each and paste them into their
                  respective fields.
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mb: 1, fontWeight: "medium" }}>
                4. Once all information in the Basic Details section is updated
                and submitted, you will not be able to edit it again.
              </Typography>

              <Typography variant="body2" sx={{ mb: 2, fontWeight: "medium" }}>
                5. The laboratories assigned to you for assessment will appear
                under the "Submitted Application" section, where you must choose
                to accept or reject each assigned application.
              </Typography>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                  Disclaimer:
                </Typography>
                <Typography variant="body2">
                  Editing your Basic Details is permitted only during your first
                  login. After submitting the information, further edits will
                  not be allowed. For every subsequent login, you will be
                  automatically redirected to the Submitted Application section
                  and if you have any allotted labs for assessment will be
                  displayed there only.
                </Typography>
              </Alert>
            </Box>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>

            <Grid item xs={12} md={6}>
              <FileUploadComponent
                fieldName="panCardFile"
                label="Upload PAN Card"
                orgId={assessorOrgId}
                section="assessor-documents"
                required={true}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                multiple={false}
                disabled={!isFormEditable}
                initialFile={formData.panCardFile}
                onChange={(fieldName, fileData) => {
                  setFormData((prev) => ({
                    ...prev,
                    panCardFile: fileData,
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FileUploadComponent
                fieldName="bankProofFile"
                label="Upload Cancel Cheque / Bank Proof"
                orgId={assessorOrgId}
                section="assessor-documents"
                required={true}
                accept=".pdf,.jpg,.jpeg,.png"
                maxSize={10}
                multiple={false}
                disabled={!isFormEditable}
                initialFile={formData.bankProofFile}
                onChange={(fieldName, fileData) => {
                  setFormData((prev) => ({
                    ...prev,
                    bankProofFile: fileData,
                  }));
                }}
              />
            </Grid>
            {(!formData.panCardFile || !formData.bankProofFile) && (
              <Button
                type="submit"
                variant="outlined"
                size="large"
                startIcon={<SaveIcon />}
              >
                {isOrgIdExist ? "Update" : "Save "}
              </Button>
            )}

            {/* Show info message when form is editable (fields not filled) */}
            {isFormEditable && assessorStatus === 1 && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Please fill GPS coordinates, NABL accreditation details and
                  other pending details to complete your profile.
                </Alert>
              </Grid>
            )}

            {/* Show warning when form is locked (all fields filled) */}
            {!isFormEditable && assessorStatus === 1 && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Your basic details are complete and locked. All required
                  information has been submitted.
                </Alert>
              </Grid>
            )}

            {/* Personal Information */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: "text.primary",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  pb: 1,
                }}
              >
                Personal Information
              </Typography>
            </Grid>
            {/* NABL Accreditation Section */}
            <Grid item xs={12}>
              <FormLabel
                component="legend"
                sx={{ fontWeight: "bold", color: "text.primary", mb: 2 }}
              >
                Is your current working organisation NABL accredited?*
              </FormLabel>
              <RadioGroup
                row
                value={formData.isNablAccredited}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    isNablAccredited: value,
                    nablAccreditationType:
                      value === "No" ? "" : formData.nablAccreditationType,
                    nablDisciplines:
                      value === "No" ? [] : formData.nablDisciplines,
                    labId: value === "No" ? "" : formData.labId,
                  });
                }}
                sx={{ gap: 3 }}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                  disabled={!isFormEditable}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label="No"
                  disabled={!isFormEditable}
                />
              </RadioGroup>
            </Grid>

            {/* Show Accreditation Type if Yes */}
            {formData.isNablAccredited === "Yes" && (
              <>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth variant="outlined" required>
                    <InputLabel>Accreditation Type</InputLabel>
                    <Select
                      value={formData.nablAccreditationType}
                      label="Accreditation Type of Organisation"
                      disabled={!isFormEditable}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                          ...formData,
                          nablAccreditationType: value,
                          nablDisciplines: [], // Reset disciplines when type changes
                        });
                      }}
                    >
                      <MenuItem value="">
                        <em>Select Type</em>
                      </MenuItem>
                      <MenuItem value="Testing">Testing</MenuItem>
                      <MenuItem value="Calibration">Calibration</MenuItem>
                      <MenuItem value="Medical">Medical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="LAB-ID"
                    value={formData.labId}
                    onChange={(e) => {
                      setFormData({ ...formData, labId: e.target.value });
                      setLabVerified(false);
                      setVerifiedLabData(null);
                      setAvailableDisciplines([]);
                    }}
                    variant="outlined"
                    placeholder="e.g., T-4337"
                    required
                    disabled={!isFormEditable}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="contained"
                            size="small"
                            onClick={verifyLabId}
                            disabled={!isFormEditable || verifyingLabId || !formData.labId || !formData.nablAccreditationType}
                            sx={{ minWidth: '80px' }}
                          >
                            {verifyingLabId ? 'Verifying...' : labVerified ? 'Verified' : 'Verify'}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {labVerified && verifiedLabData && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {verifiedLabData.name || formData.labname}
                      </Typography>
                    </Alert>
                  )}
                </Grid>

                {/* Show Disciplines if LAB-ID is verified */}
                {formData.nablAccreditationType && labVerified && (
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined" required>
                      <InputLabel>Disciplines accredited in this lab</InputLabel>
                      <Select
                        multiple
                        value={formData.nablDisciplines}
                        label="Disciplines accredited in this lab"
                        disabled={
                          !isFormEditable ||
                          verifyingLabId ||
                          availableDisciplines.length === 0 ||
                          labVerified // Disable when lab is verified
                        }
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            nablDisciplines: e.target.value,
                          });
                        }}
                        renderValue={(selected) => (
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {selected.map((id) => {
                              const discipline = availableDisciplines.find(
                                (d) => String(d.id) === String(id)
                              );
                              const label = discipline
                                ? discipline.disciplineName
                                : id;
                              return (
                                <Chip key={id} label={label} size="small" />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {verifyingLabId ? (
                          <MenuItem disabled>
                            <em>Verifying LAB-ID...</em>
                          </MenuItem>
                        ) : availableDisciplines.length === 0 ? (
                          <MenuItem disabled>
                            <em>Please verify LAB-ID first</em>
                          </MenuItem>
                        ) : (
                          availableDisciplines.map((discipline) => {
                            const disciplineId = String(discipline.id);
                            const disciplineName = discipline.disciplineName;
                            return (
                              <MenuItem key={disciplineId} value={disciplineId}>
                                <Checkbox
                                  checked={
                                    formData.nablDisciplines.indexOf(
                                      disciplineId
                                    ) > -1
                                  }
                                />
                                {disciplineName}
                              </MenuItem>
                            );
                          })
                        )}
                      </Select>
                    </FormControl>
                    {!labVerified && formData.labId && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        Please click "Verify" button to load disciplines for this LAB-ID
                      </Alert>
                    )}
                  </Grid>
                )}
              </>
            )}

            {/* Exemption from Allotment Validation */}
            {/* <Grid item xs={12}>
              <FormLabel
                component="legend"
                sx={{ fontWeight: "bold", color: "text.primary", mb: 2, mt: 2 }}
              >
                Exempt this assessor from allotment validation?*
              </FormLabel>
              <RadioGroup
                row
                value={formData.exemptFromAllotment}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    exemptFromAllotment: e.target.value,
                  });
                }}
                sx={{ gap: 3 }}
              >
                <FormControlLabel
                  value="Yes"
                  control={<Radio color="primary" />}
                  label="Yes"
                  disabled={!isFormEditable}
                />
                <FormControlLabel
                  value="No"
                  control={<Radio color="primary" />}
                  label="No"
                  disabled={!isFormEditable}
                />
              </RadioGroup>
            </Grid> */}

            {/* Address Sections */}
            <Grid item xs={12}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  color: "text.primary",
                  borderBottom: "2px solid",
                  borderColor: "primary.main",
                  pb: 1,
                }}
              >
                Address Information
              </Typography>
            </Grid>

            {/* Office Address */}
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <WorkIcon sx={{ mr: 1 }} />
                    Office Address
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Office Location Coordinates */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, textAlign: "center" }}>
                        <Button
                          variant="outlined"
                          startIcon={<MyLocationIcon />}
                          onClick={getOfficeCurrentLocation}
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            borderColor: "primary.main",
                            color: "primary.main",
                            "&:hover": {
                              borderColor: "primary.dark",
                              backgroundColor: "rgba(25, 118, 210, 0.1)",
                            },
                          }}
                        >
                          {loading
                            ? "Getting Location..."
                            : "Get Office Current Location"}
                        </Button>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Or enter coordinates manually below
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Latitude*"
                        value={formData.officeLatitude}
                        onChange={handleInputChange("officeLatitude")}
                        variant="outlined"
                        placeholder="e.g., 28.6217426"
                        helperText="Enter latitude in decimal degrees (between -90 and 90)"
                        required
                        disabled={!isFormEditable}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Longitude*"
                        value={formData.officeLongitude}
                        onChange={handleInputChange("officeLongitude")}
                        variant="outlined"
                        placeholder="e.g., 77.0882695"
                        helperText="Enter longitude in decimal degrees (between -180 and 180)"
                        required
                        disabled={!isFormEditable}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          How to find coordinates:
                        </Typography>
                        <Typography variant="body2" component="div">
                          • Open Google Maps → Right-click your office location
                          → Copy coordinates from the popup
                          <br />
                          • Or click "Get Office Current Location" button above
                          <br />• Format: Use dot (.) not comma - Correct:
                          28.6217426, Incorrect: 28,6217426
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Residential Address */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <PersonIcon sx={{ mr: 1 }} />
                    Residential Address*
                  </Typography>
                  <Grid container spacing={2}>
                    {/* Residential Location Coordinates */}
                    <Grid item xs={12}>
                      <Box sx={{ mb: 2, textAlign: "center" }}>
                        <Button
                          variant="outlined"
                          startIcon={<MyLocationIcon />}
                          onClick={getResidentialCurrentLocation}
                          disabled={loading}
                          sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            borderColor: "primary.main",
                            color: "primary.main",
                            "&:hover": {
                              borderColor: "primary.dark",
                              backgroundColor: "rgba(25, 118, 210, 0.1)",
                            },
                          }}
                        >
                          {loading
                            ? "Getting Location..."
                            : "Get Residential Current Location"}
                        </Button>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          Or enter coordinates manually below
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Latitude*"
                        value={formData.residentialLatitude}
                        onChange={handleInputChange("residentialLatitude")}
                        variant="outlined"
                        placeholder="e.g., 28.6217426"
                        helperText="Enter latitude in decimal degrees (between -90 and 90)"
                        required
                        disabled={!isFormEditable}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Longitude*"
                        value={formData.residentialLongitude}
                        onChange={handleInputChange("residentialLongitude")}
                        variant="outlined"
                        placeholder="e.g., 77.0882695"
                        helperText="Enter longitude in decimal degrees (between -180 and 180)"
                        required
                        disabled={!isFormEditable}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          How to find coordinates:
                        </Typography>
                        <Typography variant="body2" component="div">
                          • Open Google Maps → Right-click your residential
                          location → Copy coordinates from the popup
                          <br />
                          • Or click "Get Residential Current Location" button
                          above
                          <br />• Format: Use dot (.) not comma - Correct:
                          28.6217426, Incorrect: 28,6217426
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* Show Save button when form is editable AND required fields are filled */}
                  {isFormEditable &&
                    formData.officeLatitude &&
                    formData.officeLongitude &&
                    formData.residentialLatitude &&
                    formData.residentialLongitude &&
                    formData.isNablAccredited &&
                    (formData.isNablAccredited === "No" ||
                      (formData.nablAccreditationType &&
                        formData.nablDisciplines.length > 0)) && (
                      <Button
                        type="submit"
                        variant="outlined"
                        size="large"
                        startIcon={<SaveIcon />}
                      >
                        {isOrgIdExist ? "Update" : "Save "}
                      </Button>
                    )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );

  // Handle skip button
  const handleSkip = () => {
    // Save skip state to localStorage
    localStorage.setItem('assessorBioDataSkipped', 'true');
    navigate(-1);
  };

  return (
    <Box sx={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Avatar sx={{ bgcolor: "white", mr: 2 }}>
            <DescriptionIcon color="primary" />
          </Avatar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Assessor Bio-Data
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 2 }}>

        {/* Main Content (tab renderer) */}
        {renderBasicDetails()}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default AssessorBioData;
